import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { PRIMARY_OUTLET, Router, UrlTree } from '@angular/router'
import { RegistrationConfigProvider } from '@candidate/app/config/registration.config'
import { IEmployerDetailsVM } from '@candidate/app/models/employer/employer.model'
import { resumeToApplication } from '@candidate/app/services/candidate-application/application.util'
import { CandidateApplicationStageService } from '@candidate/app/services/candidate-application/candidate-application-stage.service'
import { PublicEmployerService } from '@candidate/app/services/employer/employer-public.service'
import { AbandonRegistrationService } from '@candidate/app/services/registration/abandon-registration.service'
import { RESUME_FILE_TYPES, RESUME_SIZE_MAX_BYTES, ResumeParsingService } from '@candidate/app/services/resume/resume-parsing.service'
import { SubscriptionManagementService } from '@candidate/app/services/subscription.management.service'
import { getPublicJobDetail } from '@candidate/app/store/job/job.selectors'
import * as fromReduce from '@candidate/app/store/reducers'
import { getPandoUTMFromQuery } from '@candidate/app/util/pando-utm-from-params'
import { IRegistrationResult } from '@engineering11/registration-web'
import { E11Animations } from '@engineering11/ui-lib/e11-animations'
import { E11ModalComponent } from '@engineering11/ui-lib/e11-modal'
import { isNotNil } from '@engineering11/utility'
import { ViewportService, ViewPortSize } from '@engineering11/web-utilities'
import { select, Store } from '@ngrx/store'
import jwt_decode from 'jwt-decode'
import { filter, take } from 'rxjs/operators'
import { AvailableThemes, EmployerDefaultTheme, ICandidateSource, IEmployerTheme, SessionStorageService, TokenStorage, userActions } from 'shared-lib'
import { selectors } from '../../../../store/selectors'
import { GetPublicJob } from './../../../../store/job/job.actions'

const DEFAULT_REDIRECT_PATH = 'profiles'

@Component({
  selector: 'app-register-apply',
  templateUrl: './register-apply.component.html',
  styleUrls: ['./register-apply.component.scss'],
  animations: [E11Animations.fadeIn, E11Animations.scaleSlideUp],
})
export class RegisterApplyComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('modalTerms') modalTerms!: E11ModalComponent
  @ViewChild('registrationWrapper', { read: ElementRef }) registrationWrapper!: ElementRef

  subs: SubscriptionManagementService = new SubscriptionManagementService()
  currentUser$ = this.store.pipe(select(selectors.getCurrentUser))

  jobDetail$ = this.store.select(getPublicJobDetail)
  jobPostId?: string
  maxFileSize = RESUME_SIZE_MAX_BYTES
  resumeFileTypes = RESUME_FILE_TYPES
  resumeFile?: File
  showRegistrationForm: boolean = false

  viewport = this.viewportService.getCurrentSize()
  smallerSizes: ViewPortSize[] = ['xs', 'sm']
  isMobile = this.smallerSizes.includes(this.viewport)
  activeSection: string = ''

  processing: boolean = false
  employerDetailsVM?: IEmployerDetailsVM
  employerTheme?: IEmployerTheme
  employerHeroImage?: string = ''

  candidateSource?: ICandidateSource
  uploadingResume: boolean = false

  activeTab: string = 'register'

  registrationStep: number = 1
  step1AttemptedSubmit: boolean = false
  private emailWarningObserver?: MutationObserver
  private lastDuplicateEmailWithWarning?: string

  constructor(
    private router: Router,
    private resumeParsingService: ResumeParsingService,
    private store: Store<fromReduce.job.State>,
    private tokenStorage: TokenStorage,
    private applicationStageService: CandidateApplicationStageService,
    private viewportService: ViewportService,
    private employerService: PublicEmployerService,
    private ngZone: NgZone,
    private sessionStorageService: SessionStorageService,
    private abandonRegistrationService: AbandonRegistrationService,
    private configProvider: RegistrationConfigProvider
  ) {
    const urlTree: UrlTree = this.router.parseUrl(this.router.url)
    const primary = urlTree.root.children[PRIMARY_OUTLET]
    let redirectPath = ''
    if (primary.segments.length > 1) {
      redirectPath = decodeURIComponent(primary.segments[primary.segments.length - 1].path)
      const [initialPathWithOUtQuery, queryString] = redirectPath.split('?')

      // parse utm strings for pando
      if (queryString && queryString.length) {
        const params = new URLSearchParams(queryString)
        this.candidateSource = getPandoUTMFromQuery(params)
      }

      const path = initialPathWithOUtQuery.split('/')
      const jobId = path.pop()
      this.jobPostId = jobId
      this.store.dispatch(new GetPublicJob(jobId as string))
    }
  }

  onError(error: Error): void {
    throw error
  }

  onForgottenPassword() {
    this.router.navigate(['/auth/recover'])
  }

  navigateToRegistration() {
    const redirectPath = this.buildRedirectPath()
    this.router.navigate(['/registration/register', redirectPath], { queryParamsHandling: 'merge' })
  }

  async onSubmitLogin(token: string): Promise<void> {
    const redirectPath = this.buildRedirectPath()
    const decoded: any = jwt_decode(token)
    this.store.dispatch(userActions.OnLogIn({ token, appUserId: decoded.appUserId, redirectPath }))
  }

  private buildRedirectPath() {
    const urlTree: UrlTree = this.router.parseUrl(this.router.url)
    const primary = urlTree.root.children[PRIMARY_OUTLET]
    if (!primary.segments.length) return DEFAULT_REDIRECT_PATH
    const redirectPath = primary.segments[primary.segments.length - 1].path
    if (redirectPath === 'login') return DEFAULT_REDIRECT_PATH
    else return redirectPath
  }

  ngOnInit() {
    this.jobDetail$.pipe(filter(isNotNil), take(1)).subscribe(jobDetail => {
      this.employerService
        .get(jobDetail.customerKey)
        .pipe(filter(isNotNil), take(1))
        .subscribe(employerDetailsVM => {
          this.employerHeroImage = employerDetailsVM.heroSection?.headerImage
          this.employerDetailsVM = employerDetailsVM
          if (employerDetailsVM.theme) {
            this.employerTheme = AvailableThemes.find(t => t.name === employerDetailsVM.theme)
          } else {
            this.employerTheme = EmployerDefaultTheme
          }
        })
    })
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setupEmailWarningObserver()
      this.prefillFromQueryParams()
    }, 800)
  }

  ngOnDestroy(): void {
    if (this.emailWarningObserver) {
      this.emailWarningObserver.disconnect()
    }
  }

  private findEmailWarningEl(): HTMLElement | null {
    const host = this.registrationWrapper?.nativeElement as HTMLElement | undefined
    if (!host) return null
    return host.querySelector('.e11-bg-yellow-50, [role="alert"], .e11-alert') as HTMLElement | null
  }

  private getEmailInputAndValue(): { input: HTMLInputElement | null; value: string } {
    const emailInput = (this.registrationWrapper?.nativeElement as HTMLElement | undefined)?.querySelector(
      'e11-input[name="email"] input'
    ) as HTMLInputElement | null
    const value = emailInput?.value?.trim() ?? ''
    return { input: emailInput, value }
  }

  private sendBackToStep1AndRevealWarning(): void {
    this.ngZone.run(() => {
      if (this.registrationStep !== 1) {
        const { value } = this.getEmailInputAndValue()
        this.lastDuplicateEmailWithWarning = value || undefined
        this.registrationStep = 1
      }
    })

    setTimeout(() => {
      const warning = this.findEmailWarningEl()
      warning?.scrollIntoView({ behavior: 'smooth', block: 'center' })

      const { input } = this.getEmailInputAndValue()
      input?.focus({ preventScroll: true })
      input?.select?.()
    }, 50)
  }

  setupEmailWarningObserver() {
    const sdkContainer = this.registrationWrapper?.nativeElement?.querySelector('sdk-registration-consumer') as HTMLElement | null
    if (!sdkContainer) {
      return
    }

    let pending = false
    const check = () => {
      pending = false
      if (this.registrationStep === 2 && this.findEmailWarningEl()) {
        this.sendBackToStep1AndRevealWarning()
      }
    }

    this.emailWarningObserver = new MutationObserver(() => {
      if (!pending) {
        pending = true
        requestAnimationFrame(check)
      }
    })

    this.emailWarningObserver.observe(sdkContainer, {
      childList: true,
      subtree: true,
      attributes: false,
    })
  }

  private prefillFromQueryParams(): void {
    const urlTree = this.router.parseUrl(this.router.url)
    const { email, firstName, lastName } = urlTree.queryParams as { email?: string; firstName?: string; lastName?: string }
    if (email) this.prefillField('email', email)
    if (firstName) this.prefillField('firstName', firstName)
    if (lastName) this.prefillField('lastName', lastName)
  }

  private prefillField(name: string, value: string): void {
    const wrapper = this.registrationWrapper?.nativeElement?.querySelector(`e11-input[name="${name}"]`) as HTMLElement | null
    if (!wrapper) return
    const input = wrapper.querySelector('input') as HTMLInputElement | null
    if (!input) return
    input.value = value
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  continueToStep2() {
    const emailInputWrapper = this.registrationWrapper?.nativeElement?.querySelector('e11-input[name="email"]')
    const firstNameInputWrapper = this.registrationWrapper?.nativeElement?.querySelector('e11-input[name="firstName"]')
    const lastNameInputWrapper = this.registrationWrapper?.nativeElement?.querySelector('e11-input[name="lastName"]')

    const emailInput = emailInputWrapper?.querySelector('input') as HTMLInputElement
    const firstNameInput = firstNameInputWrapper?.querySelector('input') as HTMLInputElement
    const lastNameInput = lastNameInputWrapper?.querySelector('input') as HTMLInputElement

    const emailValue = emailInput?.value?.trim()
    const firstNameValue = firstNameInput?.value?.trim()
    const lastNameValue = lastNameInputWrapper?.querySelector('input') ? lastNameInput?.value?.trim() : lastNameInput?.value?.trim()

    const bannerExists = !!this.findEmailWarningEl()
    if (bannerExists && this.lastDuplicateEmailWithWarning && emailValue === this.lastDuplicateEmailWithWarning) {
      const warning = this.findEmailWarningEl()
      warning?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const { input } = this.getEmailInputAndValue()
      input?.focus({ preventScroll: true })
      input?.select?.()
      return
    }

    if (emailValue && firstNameValue && lastNameValue) {
      this.abandonRegistrationService.queueAbandonNotification({
        email: emailValue,
        firstName: firstNameValue,
        lastName: lastNameValue,
        jobPostId: this.jobPostId,
        authTenantId: this.configProvider.config.authTenantId,
      })
      this.registrationStep = 2
      return
    }

    const inputEvent = new Event('input', { bubbles: true })
    const blurEvent = new Event('blur', { bubbles: true })

    if (!emailValue && emailInput) {
      emailInput.dispatchEvent(inputEvent)
      emailInput.dispatchEvent(blurEvent)
    }
    if (!firstNameValue && firstNameInput) {
      firstNameInput.dispatchEvent(inputEvent)
      firstNameInput.dispatchEvent(blurEvent)
    }
    if (!lastNameValue && lastNameInput) {
      lastNameInput.dispatchEvent(inputEvent)
      lastNameInput.dispatchEvent(blurEvent)
    }
  }

  backToStep1() {
    this.registrationStep = 1
  }

  async onSubmit(result: IRegistrationResult) {
    if (result) {
      this.tokenStorage.setAccessToken(result.authToken)
      this.tokenStorage.setItem('user', JSON.stringify(result.appUser))

      if (this.resumeFile && this.jobPostId) {
        this.uploadingResume = true
        const parsedResume = await this.resumeParsingService.parse(this.resumeFile, result.appUser.customerKey, result.appUser.id)
        const applicationStage = resumeToApplication(parsedResume, this.jobPostId, this.candidateSource)
        await this.applicationStageService.upsert(applicationStage)
      }

      const urlTree: UrlTree = this.router.parseUrl(this.router.url)
      const primary = urlTree.root.children[PRIMARY_OUTLET]
      let redirectPath = ''
      if (primary.segments.length > 1) {
        redirectPath = decodeURIComponent(primary.segments[primary.segments.length - 1].path)
        const [initialPathWithOUtQuery] = redirectPath.split('?')
        redirectPath = initialPathWithOUtQuery
      }
      this.sessionStorageService.setItem('fromRegisterApply', true)
      this.router.navigate([redirectPath], { queryParamsHandling: 'merge', queryParams: this.candidateSource })
    }
  }

  termsClicked() {
    this.modalTerms.open()
  }

  navigateToLogin() {
    const urlTree: UrlTree = this.router.parseUrl(this.router.url)
    const primary = urlTree.root.children[PRIMARY_OUTLET]
    let redirectPath = undefined
    if (primary.segments.length > 1) {
      redirectPath = primary.segments[primary.segments.length - 1].path
    }
    this.router.navigate(['/auth/login', redirectPath], {
      queryParams: urlTree.queryParams,
      queryParamsHandling: 'merge',
    })
  }

  clearSelectedFile() {
    this.resumeFile = undefined
    this.showRegistrationForm = false
  }

  switchShowRegistrationForm(value: boolean) {
    this.showRegistrationForm = value
  }

  back() {
    this.showRegistrationForm = false
  }

  register() {
    this.showRegistrationForm = true
  }
}
